import type { InteractionState } from '@repo/auth-contracts';
import type { LegalDocument } from './steps/require-terms-step';

/**
 * The screen the SPA is showing. All decisions are made server-side (ADR
 * 0010); this only mirrors what the interaction API reported plus local
 * pending/error state.
 */
export type Step =
  | { kind: 'loading' }
  | { kind: 'email' }
  /**
   * The same email form under sign-up wording (ADR 0019). A *view*, not a
   * second path: it posts to the same endpoint and the server cannot tell the
   * two apart — which is what stops the wording from leaking whether an
   * address already has an account.
   */
  | { kind: 'signup' }
  | { kind: 'code'; email: string }
  | { kind: 'complete-profile' }
  | { kind: 'password' }
  | { kind: 'offer-password' }
  | { kind: 'offer-passkey' }
  /**
   * The mandatory age gate for an account that never passed it — a migrated
   * one (ADR 0021). Unlike the two offers above it has no skip, and the login
   * is held server-side until it is answered.
   */
  | { kind: 'require-birthdate' }
  | { kind: 'require-terms'; documents: LegalDocument[] }
  | { kind: 'social-pending'; reason: string }
  | {
      kind: 'consent';
      clientName: string;
      scopes: string[];
      grantedScopes: string[];
    }
  | { kind: 'finishing' }
  | { kind: 'error'; code: string };

export interface FlowState {
  step: Step;
  /** A request is in flight — submit buttons disable, no double-posts. */
  pending: boolean;
  /** Inline error code for the current step (rendered via `messageFor`). */
  errorCode: string | null;
  /** Confirmation after a resend, cleared on the next action. */
  notice: 'code-resent' | null;
  csrfToken: string | null;
  /** Which social buttons to show — server-driven, never inferred. */
  socialProviders: string[];
  passkeysEnabled: boolean;
  /** The server's age gate, so the hint matches what submit will enforce. */
  minimumAge: number;
  /** Names a social provider supplied, to seed the profile step (ADR 0019). */
  profilePrefill: { givenName?: string; familyName?: string } | null;
}

export type FlowEvent =
  | { type: 'loaded'; state: InteractionState }
  | { type: 'load-failed'; code: string }
  | { type: 'submit' }
  | { type: 'code-sent'; email: string }
  | { type: 'profile-required' }
  | { type: 'show-password' }
  | { type: 'show-email' }
  | { type: 'show-signup' }
  /**
   * The browser went Back or Forward to a step this session already showed.
   * Restores the view without re-running anything: history holds only the
   * steps that are safe to return to (see `NAVIGABLE_STEPS`).
   */
  | { type: 'restore'; step: Step }
  | { type: 'offer-password' }
  | { type: 'require-birthdate' }
  | { type: 'require-terms'; documents: LegalDocument[] }
  | { type: 'offer-passkey' }
  | { type: 'social-pending'; reason: string }
  | { type: 'code-resent' }
  | { type: 'finished' }
  | { type: 'failed'; code: string }
  | { type: 'fatal'; code: string }
  | { type: 'restart' };

export const initialFlowState: FlowState = {
  step: { kind: 'loading' },
  pending: false,
  errorCode: null,
  notice: null,
  csrfToken: null,
  socialProviders: [],
  passkeysEnabled: false,
  minimumAge: 18,
  profilePrefill: null,
};

/** Codes that invalidate the whole interaction rather than one submission. */
const FATAL_CODES = new Set(['interaction_not_found', 'csrf']);

export function flowReducer(state: FlowState, event: FlowEvent): FlowState {
  switch (event.type) {
    case 'loaded': {
      const {
        prompt,
        client,
        scopes,
        grantedScopes,
        csrfToken,
        socialProviders,
        passkeysEnabled,
      } = event.state;
      return {
        ...state,
        pending: false,
        errorCode: null,
        csrfToken,
        socialProviders,
        passkeysEnabled,
        minimumAge: event.state.minimumAge ?? state.minimumAge,
        profilePrefill: event.state.profilePrefill ?? null,
        // A reported mid-flow position wins over the cold-start choice: the
        // server only reports LIVE state (an unconsumed code, an open pending
        // sign-up), so resuming it is never a screen whose submit must fail.
        step:
          prompt === 'consent'
            ? {
                kind: 'consent',
                clientName: client.clientName,
                scopes,
                grantedScopes,
              }
            : event.state.pending?.step === 'complete-profile'
              ? { kind: 'complete-profile' }
              : event.state.pending?.step === 'code'
                ? { kind: 'code', email: event.state.pending.email }
                : event.state.screenHint === 'signup'
                  ? { kind: 'signup' }
                  : { kind: 'email' },
      };
    }

    case 'load-failed':
      return {
        ...state,
        pending: false,
        step: { kind: 'error', code: event.code },
      };

    case 'submit':
      // The error is KEPT during the retry on purpose: clearing it unmounted
      // the alert, and the next failure re-inserted it — shifting the whole
      // form at exactly the moment the user was reaching for a control. Kept,
      // a repeat failure replaces the text in place with no layout shift; the
      // success paths all change step or set states that clear it.
      return { ...state, pending: true, notice: null };

    case 'code-sent':
      return {
        ...state,
        pending: false,
        errorCode: null,
        notice: null,
        step: { kind: 'code', email: event.email },
      };

    case 'show-password':
      return {
        ...state,
        pending: false,
        errorCode: null,
        notice: null,
        step: { kind: 'password' },
      };

    case 'show-email':
      return {
        ...state,
        pending: false,
        errorCode: null,
        notice: null,
        step: { kind: 'email' },
      };

    case 'restore':
      return {
        ...state,
        pending: false,
        errorCode: null,
        notice: null,
        step: event.step,
      };

    case 'show-signup':
      return {
        ...state,
        pending: false,
        errorCode: null,
        notice: null,
        step: { kind: 'signup' },
      };

    case 'social-pending':
      return {
        ...state,
        pending: false,
        errorCode: null,
        notice: null,
        step: { kind: 'social-pending', reason: event.reason },
      };

    case 'offer-passkey':
      return {
        ...state,
        pending: false,
        errorCode: null,
        notice: null,
        step: { kind: 'offer-passkey' },
      };

    case 'offer-password':
      return {
        ...state,
        pending: false,
        errorCode: null,
        notice: null,
        step: { kind: 'offer-password' },
      };

    case 'require-birthdate':
      return {
        ...state,
        pending: false,
        errorCode: null,
        notice: null,
        step: { kind: 'require-birthdate' },
      };

    case 'require-terms':
      return {
        ...state,
        pending: false,
        errorCode: null,
        notice: null,
        step: { kind: 'require-terms', documents: event.documents },
      };
    case 'profile-required':
      return {
        ...state,
        pending: false,
        errorCode: null,
        notice: null,
        step: { kind: 'complete-profile' },
      };

    case 'code-resent':
      return {
        ...state,
        pending: false,
        errorCode: null,
        notice: 'code-resent',
      };

    case 'finished':
      return {
        ...state,
        pending: false,
        errorCode: null,
        step: { kind: 'finishing' },
      };

    case 'failed':
      // A dead interaction cannot be retried in place — show the error screen.
      return FATAL_CODES.has(event.code)
        ? {
            ...state,
            pending: false,
            step: { kind: 'error', code: event.code },
          }
        : { ...state, pending: false, errorCode: event.code };

    case 'fatal':
      return {
        ...state,
        pending: false,
        step: { kind: 'error', code: event.code },
      };

    case 'restart':
      return {
        ...state,
        pending: false,
        errorCode: null,
        notice: null,
        step: { kind: 'email' },
      };

    default:
      return state;
  }
}

/**
 * The steps a Back button may return to.
 *
 * Deliberately only the ones **before** an identity is proven. Every later
 * step sits behind something already spent — a consumed one-time code, a held
 * login — so restoring it would show a screen whose submit is guaranteed to
 * fail. Those steps push no history entry at all, which means Back from them
 * lands on the last pre-authentication view instead of a stale one.
 */
export const NAVIGABLE_STEPS = ['email', 'signup', 'password', 'code'] as const;

export type NavigableStepKind = (typeof NAVIGABLE_STEPS)[number];

export function isNavigableStep(kind: Step['kind']): kind is NavigableStepKind {
  return (NAVIGABLE_STEPS as readonly string[]).includes(kind);
}

/**
 * The steps that only exist because an identity has already been proven.
 *
 * Deliberately NOT "everything that is not navigable": `loading` is the very
 * first step of every session and `error` can be reached before anyone has
 * typed anything, so treating either as past-identity would arm the guard
 * below before the user has done a thing — which is exactly the bug this list
 * replaces.
 */
export const POST_IDENTITY_STEPS = [
  'complete-profile',
  'offer-password',
  'offer-passkey',
  'require-birthdate',
  'require-terms',
  'social-pending',
  'consent',
  'finishing',
] as const;

export function isPostIdentityStep(kind: Step['kind']): boolean {
  return (POST_IDENTITY_STEPS as readonly string[]).includes(kind);
}
